import { useEffect, useState, type Dispatch, type MutableRefObject, type SetStateAction } from "react";
import type { Auth } from "firebase/auth";
import { onIdTokenChanged } from "firebase/auth";
import type { PageKey } from "../lib/menu";

type Role = "admin" | "user" | "customer" | "unassigned";
export type AccountStatus = "pending" | "active" | "disabled" | "none";
export type AppUser = { uid: string; email: string; emailVerified: boolean; role: Role; status: AccountStatus };

type Options<T extends { id: string }> = {
  auth: Auth;
  fetchManufacturers: () => Promise<void>;
  fetchCustomers: () => Promise<void>;
  setItems: Dispatch<SetStateAction<T[]>>;
  fetchFirstPage: () => Promise<void>;
  startHomeSentinel?: () => void;
  stopHomeSentinel?: () => void;
  activePageRef: MutableRefObject<PageKey>;
  setActivePage: Dispatch<SetStateAction<PageKey>>;
};

export function useAuthState<T extends { id: string }>({
  auth,
  fetchManufacturers,
  fetchCustomers,
  setItems,
  fetchFirstPage,
  startHomeSentinel,
  stopHomeSentinel,
  activePageRef,
  setActivePage,
}: Options<T>) {
  const [user, setUser] = useState<AppUser | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [isCustomerPortal, setIsCustomerPortal] = useState<boolean>(false);
  const [customerStatus, setCustomerStatus] = useState<"pending" | "active" | "disabled" | "none">("none");
  const [customerKeys, setCustomerKeys] = useState<string[]>([]);

  useEffect(() => {
    const unsub = onIdTokenChanged(auth, async (u) => {
      // Read the token that triggered this event. Forcing a refresh here can
      // trigger onIdTokenChanged again and exhaust Firebase Auth quotas.
      const token = u ? await u.getIdTokenResult() : null;
      if (u) {
        const role = (token!.claims.role as Role) || "unassigned";
        const status = (token!.claims.status as AccountStatus | undefined) ?? "none";
        setUser({ uid: u.uid, email: u.email || "", emailVerified: u.emailVerified, role, status });
      } else {
        setUser(null);
      }

      setAuthReady(true);
      stopHomeSentinel?.();

      if (!u) {
        setIsCustomerPortal(false);
        setCustomerStatus("none");
        setCustomerKeys([]);
        setItems([]);
        return;
      }

      const claims = token!.claims;
      const role = claims?.role as string | undefined;
      const status = claims?.status as ("pending" | "active" | "disabled") | undefined;
      const keysRaw = claims?.customerKeys as unknown;
      const keysArr = Array.isArray(keysRaw) ? (keysRaw as any[]).map(String) : [];

      setCustomerKeys(keysArr);
      setCustomerStatus(status ?? "none");
      setIsCustomerPortal(role === "customer" && status === "active");

      try {
        const tasks: Promise<any>[] = [];
        if (status === "active") {
          tasks.push(fetchManufacturers().catch(() => undefined));
          if (role === "admin") tasks.push(fetchCustomers().catch(() => undefined));
        }
        await Promise.all(tasks);
      } catch {
        /* ignore preload errors */
      }

      setTimeout(async () => {
        const u2 = auth.currentUser;
        const canQuery = u2 && u2.emailVerified && status === "active" && (role === "admin" || role === "user" || role === "customer");
        if (!canQuery) return;
        if (activePageRef.current === "home") {
          await fetchFirstPage();
          startHomeSentinel?.();
        }
      }, 0);
    });
    return () => unsub();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // ^ Vi vet att funktionerna inuti är stabila callbacks eller setters

  useEffect(() => {
    if (!isCustomerPortal) return;

    if (activePageRef.current !== "home" && activePageRef.current !== "rapporter") {
      setActivePage("home");
      activePageRef.current = "home";
    }
  }, [isCustomerPortal, activePageRef, setActivePage]);

  return {
    user,
    authReady,
    isCustomerPortal,
    customerStatus,
    customerKeys,
    setCustomerKeys,
  };
}
